import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { DollarSign, Save, Copy, Search, Download, Upload } from 'lucide-react';
import {
    proceduresApi,
    locationsApi,
    costSectionsApi,
    costItemsApi,
    pricingApi,
    roomTypesApi,
    icuRatesApi
} from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';
import { toast } from '@/utils/toast';
import { ImportPreviewDialog } from '@/components/pricing/ImportPreviewDialog';
import { ValidationErrorsDialog } from '@/components/pricing/ValidationErrorsDialog';

type TabType = 'cost-items' | 'room-rates' | 'icu-rates';

export function PricingPage() {
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [selectedProcedureId, setSelectedProcedureId] = useState('');
    const [selectedConfigId, setSelectedConfigId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<TabType>('cost-items');
    const [showImportPreview, setShowImportPreview] = useState(false);
    const [importPreviewData, setImportPreviewData] = useState<any>(null);
    const [importToken, setImportToken] = useState<string>('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [validationErrors, setValidationErrors] = useState<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: procedures = [] } = useQuery({
        queryKey: ['procedures'],
        queryFn: () => proceduresApi.list(),
    });

    const { data: sections = [] } = useQuery({
        queryKey: ['cost-sections', selectedLocationId],
        queryFn: () => costSectionsApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: roomTypes = [] } = useQuery({
        queryKey: ['room-types', selectedLocationId],
        queryFn: () => roomTypesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: roomRateOverrides = [] } = useQuery({
        queryKey: ['room-rate-overrides', selectedConfigId],
        queryFn: () => roomTypesApi.getOverrides(selectedConfigId),
        enabled: !!selectedConfigId,
    });

    const { data: icuRates = [] } = useQuery({
        queryKey: ['icu-rates', selectedLocationId],
        queryFn: () => icuRatesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: icuRateOverrides = [] } = useQuery({
        queryKey: ['icu-rate-overrides', selectedConfigId],
        queryFn: () => icuRatesApi.getOverrides(selectedConfigId),
        enabled: !!selectedConfigId,
    });

    // Fetch all cost items for all sections
    const sectionIds = sections.map((s: any) => s.id);
    const itemQueries = useQuery({
        queryKey: ['all-cost-items', selectedLocationId, sectionIds],
        queryFn: async () => {
            if (!sectionIds.length) return [];
            const allItems = await Promise.all(
                sectionIds.map((sectionId: string) => costItemsApi.list(sectionId))
            );
            return allItems.flat();
        },
        enabled: sectionIds.length > 0,
    });

    const { data: existingPricing = [], isLoading: pricingLoading } = useQuery({
        queryKey: ['pricing', selectedConfigId],
        queryFn: () => pricingApi.get(selectedConfigId),
        enabled: !!selectedConfigId,
    });

    // Merge cost items with existing pricing
    const allCostItems = itemQueries.data || [];
    const pricing = allCostItems.map((item: any) => {
        const existing = existingPricing.find((p: any) => p.costItemId === item.id);
        const section = sections.find((s: any) => s.id === item.costSectionId);

        return {
            costItemId: item.id,
            itemName: item.name,
            unit: item.unit,
            sectionName: section?.name || 'Other',
            sectionType: section?.sectionType || 'FIXED',
            percentageBase: section?.percentageBase,
            amount: existing?.amount || null,
            percentage: existing?.percentage || null,
            unitPrice: existing?.unitPrice || null,
            defaultQuantity: existing?.defaultQuantity || null,
            // Pass through defaults for UI display
            defaultAmountConfig: item.defaultAmount,
            defaultPercentageConfig: item.defaultPercentage,
            defaultUnitPriceConfig: item.defaultUnitPrice,
            roomTypeOverrides: existing?.roomTypeOverrides,
        };
    });

    // Merge room types with overrides
    const roomRates = roomTypes.map((room: any) => {
        const override = roomRateOverrides.find((o: any) => o.roomTypeId === room.id);
        return {
            roomTypeId: room.id,
            roomName: room.name,
            defaultRate: room.defaultRate,
            markupPercentage: room.markupPercentage || 0,
            overrideRate: override !== undefined ? override.overrideRate : null,
        };
    });

    // Merge ICU rates with overrides
    const icuRatesList = icuRates.map((icu: any) => {
        const override = icuRateOverrides.find((o: any) => o.icuRateId === icu.id);
        return {
            icuRateId: icu.id,
            icuName: icu.name,
            description: icu.description,
            defaultRate: icu.dailyRate,
            overrideRate: override !== undefined ? override.overrideRate : null,
        };
    });

    const updatePricingMutation = useMutation({
        mutationFn: ({ configId, items }: { configId: string; items: any[] }) =>
            pricingApi.update(configId, items),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricing'] });
            toast.success('Pricing saved successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to save pricing');
        },
    });

    const updateRoomRatesMutation = useMutation({
        mutationFn: ({ configId, overrides }: { configId: string; overrides: any[] }) =>
            roomTypesApi.setOverrides(configId, overrides),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['room-rate-overrides'] });
            toast.success('Room rates saved successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to save room rates');
        },
    });

    const updateIcuRatesMutation = useMutation({
        mutationFn: ({ configId, overrides }: { configId: string; overrides: any[] }) =>
            icuRatesApi.setOverrides(configId, overrides),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['icu-rate-overrides'] });
            toast.success('ICU rates saved successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to save ICU rates');
        },
    });

    const clonePricingMutation = useMutation({
        mutationFn: ({ sourceConfigId, targetConfigId }: { sourceConfigId: string; targetConfigId: string }) =>
            pricingApi.clone(sourceConfigId, targetConfigId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricing'] });
            toast.success('Pricing cloned successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to clone pricing');
        },
    });

    const importPreviewMutation = useMutation({
        mutationFn: ({ locationId, file }: { locationId: string; file: File }) =>
            pricingApi.importPreview(locationId, file),
        onSuccess: (data) => {
            setImportPreviewData(data);
            setImportToken(data.importToken);
            setShowImportPreview(true);
        },
        onError: (error: any) => {
            const errors = error.response?.data?.errors;
            if (errors && errors.length > 0) {
                setValidationErrors(errors);
                setShowValidationErrors(true);
            } else {
                toast.error(error.response?.data?.error || 'Failed to preview import');
            }
        },
    });

    const importApplyMutation = useMutation({
        mutationFn: ({ importToken, locationId }: { importToken: string; locationId: string }) =>
            pricingApi.importApply(importToken, locationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricing'] });
            queryClient.invalidateQueries({ queryKey: ['procedures'] });
            queryClient.invalidateQueries({ queryKey: ['cost-sections'] });
            queryClient.invalidateQueries({ queryKey: ['cost-items'] });
            setShowImportPreview(false);
            setImportPreviewData(null);
            setImportToken('');
            toast.success('Import completed successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to apply import');
        },
    });

    const { register, handleSubmit, setValue, watch } = useForm();

    // Handle location change
    const handleLocationChange = (locationId: string) => {
        setSelectedLocationId(locationId);
        setSelectedProcedureId('');
        setSelectedConfigId('');
        setSearchQuery('');
    };

    // Handle procedure change
    const handleProcedureChange = (procedureId: string) => {
        setSelectedProcedureId(procedureId);
        const procedure = procedures.find((p: any) => p.id === procedureId);
        if (procedure) {
            const config = procedure.configs?.find((c: any) => c.locationId === selectedLocationId);
            setSelectedConfigId(config?.id || '');
        }
    };

    const onSubmitCostItems = (data: any) => {
        const items = pricing.map((item: any) => ({
            costItemId: item.costItemId,
            amount: data[`amount_${item.costItemId}`] ? parseFloat(data[`amount_${item.costItemId}`]) : null,
            percentage: data[`percentage_${item.costItemId}`] ? parseFloat(data[`percentage_${item.costItemId}`]) : null,
            unitPrice: data[`unitPrice_${item.costItemId}`] ? parseFloat(data[`unitPrice_${item.costItemId}`]) : null,
            defaultQuantity: data[`defaultQuantity_${item.costItemId}`] ? parseFloat(data[`defaultQuantity_${item.costItemId}`]) : null,
        }));

        updatePricingMutation.mutate({ configId: selectedConfigId, items });
    };

    const onSubmitRoomRates = async (data: any) => {
        // Separate room rates into: overrides to set, overrides to delete, and zero-rate overrides
        const overridesToSet: any[] = [];
        const roomsToDelete: any[] = [];
        const zeroRateOverrides: any[] = [];

        for (const room of roomRates) {
            const fieldValue = data[`override_${room.roomTypeId}`];
            const hasExistingOverride = room.overrideRate !== null;

            // Check if field is blank (empty string, null, undefined, or whitespace)
            const isBlank = !fieldValue || String(fieldValue).trim() === '';

            if (isBlank) {
                // Blank value - delete override if it exists
                if (hasExistingOverride) {
                    roomsToDelete.push({
                        roomTypeId: room.roomTypeId,
                        roomName: room.roomName,
                    });
                }
            } else {
                const rateValue = parseFloat(fieldValue);
                if (isNaN(rateValue)) {
                    // Invalid number, skip
                    continue;
                } else if (rateValue === 0) {
                    // Zero value - needs confirmation
                    zeroRateOverrides.push({
                        roomName: room.roomName,
                        roomTypeId: room.roomTypeId,
                        overrideRate: 0,
                    });
                } else if (rateValue > 0) {
                    // Positive value - set override
                    overridesToSet.push({
                        procedureConfigId: selectedConfigId,
                        roomTypeId: room.roomTypeId,
                        overrideRate: rateValue,
                    });
                }
            }
        }

        // Handle deletion with confirmation
        if (roomsToDelete.length > 0) {
            const roomNames = roomsToDelete.map(r => r.roomName).join(', ');
            const plural = roomsToDelete.length > 1;
            const confirmed = window.confirm(
                `Remove custom rate${plural ? 's' : ''} for: ${roomNames}\n\n` +
                `${plural ? 'These room types' : 'This room type'} will use the default rate${plural ? 's' : ''} for this procedure.\n` +
                `Are you sure you want to continue?`
            );

            if (!confirmed) {
                toast.error('Room rates not saved - deletion cancelled');
                return;
            }
        }

        // Handle zero-rate overrides with confirmation
        if (zeroRateOverrides.length > 0) {
            const roomNames = zeroRateOverrides.map(o => o.roomName).join(', ');
            const plural = zeroRateOverrides.length > 1;
            const confirmed = window.confirm(
                `Setting zero rate for: ${roomNames}\n\n` +
                `This will make ${plural ? 'these room types' : 'this room type'} FREE for this procedure.\n` +
                `Are you sure you want to continue?`
            );

            if (confirmed) {
                // Add confirmed zero-rate overrides to the set list
                overridesToSet.push(...zeroRateOverrides.map(o => ({
                    procedureConfigId: selectedConfigId,
                    roomTypeId: o.roomTypeId,
                    overrideRate: o.overrideRate,
                })));
            } else {
                toast.error('Room rates not saved - zero rate confirmation cancelled');
                return;
            }
        }

        try {
            // Delete overrides first
            for (const room of roomsToDelete) {
                await roomTypesApi.deleteOverride(selectedConfigId, room.roomTypeId);
            }

            // Then set/update overrides
            if (overridesToSet.length > 0) {
                await updateRoomRatesMutation.mutateAsync({ configId: selectedConfigId, overrides: overridesToSet });
            } else if (roomsToDelete.length > 0) {
                // If we only deleted, invalidate queries and show success
                queryClient.invalidateQueries({ queryKey: ['room-rate-overrides'] });
                const plural = roomsToDelete.length > 1;
                toast.success(`Room rate override${plural ? 's' : ''} removed successfully`);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to save room rates');
        }
    };

    const onSubmitIcuRates = async (data: any) => {
        // Separate ICU rates into: overrides to set, overrides to delete, and zero-rate overrides
        const overridesToSet: any[] = [];
        const icusToDelete: any[] = [];
        const zeroRateOverrides: any[] = [];

        for (const icu of icuRatesList) {
            const fieldValue = data[`override_${icu.icuRateId}`];
            const hasExistingOverride = icu.overrideRate !== null;

            // Check if field is blank (empty string, null, undefined, or whitespace)
            const isBlank = !fieldValue || String(fieldValue).trim() === '';

            if (isBlank) {
                // Blank value - delete override if it exists
                if (hasExistingOverride) {
                    icusToDelete.push({
                        icuRateId: icu.icuRateId,
                        icuName: icu.icuName,
                    });
                }
            } else {
                const rateValue = parseFloat(fieldValue);
                if (isNaN(rateValue)) {
                    // Invalid number, skip
                    continue;
                } else if (rateValue === 0) {
                    // Zero value - needs confirmation
                    zeroRateOverrides.push({
                        icuName: icu.icuName,
                        icuRateId: icu.icuRateId,
                        overrideRate: 0,
                    });
                } else if (rateValue > 0) {
                    // Positive value - set override
                    overridesToSet.push({
                        procedureConfigId: selectedConfigId,
                        icuRateId: icu.icuRateId,
                        overrideRate: rateValue,
                    });
                }
            }
        }

        // Handle deletion with confirmation
        if (icusToDelete.length > 0) {
            const icuNames = icusToDelete.map(i => i.icuName).join(', ');
            const plural = icusToDelete.length > 1;
            const confirmed = window.confirm(
                `Remove custom rate${plural ? 's' : ''} for: ${icuNames}\n\n` +
                `${plural ? 'These ICU types' : 'This ICU type'} will use the default rate${plural ? 's' : ''} for this procedure.\n` +
                `Are you sure you want to continue?`
            );

            if (!confirmed) {
                toast.error('ICU rates not saved - deletion cancelled');
                return;
            }
        }

        // Handle zero-rate overrides with confirmation
        if (zeroRateOverrides.length > 0) {
            const icuNames = zeroRateOverrides.map(o => o.icuName).join(', ');
            const plural = zeroRateOverrides.length > 1;
            const confirmed = window.confirm(
                `Setting zero rate for: ${icuNames}\n\n` +
                `This will make ${plural ? 'these ICU types' : 'this ICU type'} FREE for this procedure.\n` +
                `Are you sure you want to continue?`
            );

            if (confirmed) {
                // Add confirmed zero-rate overrides to the set list
                overridesToSet.push(...zeroRateOverrides.map(o => ({
                    procedureConfigId: selectedConfigId,
                    icuRateId: o.icuRateId,
                    overrideRate: o.overrideRate,
                })));
            } else {
                toast.error('ICU rates not saved - zero rate confirmation cancelled');
                return;
            }
        }

        try {
            // Delete overrides first
            for (const icu of icusToDelete) {
                await icuRatesApi.deleteOverride(selectedConfigId, icu.icuRateId);
            }

            // Then set/update overrides
            if (overridesToSet.length > 0) {
                await updateIcuRatesMutation.mutateAsync({ configId: selectedConfigId, overrides: overridesToSet });
            } else if (icusToDelete.length > 0) {
                // If we only deleted, invalidate queries and show success
                queryClient.invalidateQueries({ queryKey: ['icu-rate-overrides'] });
                const plural = icusToDelete.length > 1;
                toast.success(`ICU rate override${plural ? 's' : ''} removed successfully`);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to save ICU rates');
        }
    };

    // Filter pricing based on search
    const filteredPricing = pricing.filter((item: any) =>
        item.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sectionName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group by section
    const groupedPricing = filteredPricing.reduce((acc: Record<string, any[]>, item: any) => {
        if (!acc[item.sectionName]) {
            acc[item.sectionName] = [];
        }
        acc[item.sectionName].push(item);
        return acc;
    }, {});

    // Get active procedures for location
    const activeProcedures = procedures.filter((p: any) =>
        p.configs?.some((c: any) => c.locationId === selectedLocationId && c.isActive)
    );

    // Handle export
    const handleExport = async () => {
        if (!selectedLocationId) {
            toast.error('Please select a location first');
            return;
        }

        try {
            const blob = await pricingApi.exportExcel(selectedLocationId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const location = locations.find((l: any) => l.id === selectedLocationId);
            const locationName = location?.name || 'unknown';
            const date = new Date().toISOString().split('T')[0];
            a.download = `pricing-${locationName.replace(/[^a-z0-9]/gi, '-')}-${date}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Pricing exported successfully');
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to export pricing');
        }
    };

    // Handle import file selection
    const handleImportClick = () => {
        if (!selectedLocationId) {
            toast.error('Please select a location first');
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Please select an Excel file (.xlsx or .xls)');
            return;
        }

        importPreviewMutation.mutate({ locationId: selectedLocationId, file });

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleImportConfirm = () => {
        if (!importToken) return;
        importApplyMutation.mutate({ importToken, locationId: selectedLocationId });
    };

    const handleImportCancel = () => {
        setShowImportPreview(false);
        setImportPreviewData(null);
        setImportToken('');
        importPreviewMutation.reset();
        importApplyMutation.reset();
    };

    const handleValidationErrorsClose = () => {
        setShowValidationErrors(false);
        setValidationErrors([]);
        importPreviewMutation.reset();
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Pricing Configuration</h1>
                    <p className="text-slate-600">Set prices for procedures at each location</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleExport}
                        disabled={!selectedLocationId}
                        className="btn btn-secondary btn-sm"
                        title="Export pricing to Excel"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                    <button
                        onClick={handleImportClick}
                        disabled={!selectedLocationId || importPreviewMutation.isPending}
                        className="btn btn-primary btn-sm"
                        title="Import pricing from Excel"
                    >
                        <Upload className="w-4 h-4" />
                        {importPreviewMutation.isPending ? 'Validating...' : 'Import'}
                    </button>
                </div>
            </div>

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                className="hidden"
            />

            {/* Import Preview Dialog */}
            <ImportPreviewDialog
                isOpen={showImportPreview}
                onClose={handleImportCancel}
                onConfirm={handleImportConfirm}
                summary={importPreviewData?.summary || null}
                changes={importPreviewData?.changes || null}
                isApplying={importApplyMutation.isPending}
            />

            {/* Validation Errors Dialog */}
            <ValidationErrorsDialog
                isOpen={showValidationErrors}
                onClose={handleValidationErrorsClose}
                errors={validationErrors}
            />

            {/* Location & Procedure Selectors */}
            <div className="card">
                <div className="card-body space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Location *</label>
                            <select
                                className="input"
                                value={selectedLocationId}
                                onChange={(e) => handleLocationChange(e.target.value)}
                            >
                                <option value="">Select location...</option>
                                {locations.map((location: any) => (
                                    <option key={location.id} value={location.id}>
                                        {location.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="label">Procedure *</label>
                            <select
                                key={`procedure-${selectedLocationId}`}
                                className="input"
                                value={selectedProcedureId}
                                onChange={(e) => handleProcedureChange(e.target.value)}
                                disabled={!selectedLocationId}
                            >
                                <option value="">Select procedure...</option>
                                {activeProcedures.map((procedure: any) => (
                                    <option key={procedure.id} value={procedure.id}>
                                        {procedure.name}
                                    </option>
                                ))}
                            </select>
                            {selectedLocationId && activeProcedures.length === 0 && (
                                <p className="text-xs text-slate-500 mt-1">
                                    No active procedures for this location
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {selectedConfigId && (
                <>
                    {/* Tabs */}
                    <div className="border-b border-slate-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setActiveTab('cost-items')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'cost-items'
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                Cost Items
                            </button>
                            <button
                                onClick={() => setActiveTab('room-rates')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'room-rates'
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                Room Rates
                            </button>
                            <button
                                onClick={() => setActiveTab('icu-rates')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'icu-rates'
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                ICU Rates
                            </button>
                        </nav>
                    </div>

                    {/* Cost Items Tab */}
                    {activeTab === 'cost-items' && (
                        <div className="card">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Cost Item Pricing</h2>
                                <div className="flex gap-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search items..."
                                            className="input pl-10 w-64"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <form key={`cost-items-form-${selectedConfigId}`} onSubmit={handleSubmit(onSubmitCostItems)}>
                                <div className="card-body space-y-6">
                                    {pricingLoading ? (
                                        <div className="space-y-2">
                                            {[...Array(5)].map((_, i) => (
                                                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
                                            ))}
                                        </div>
                                    ) : Object.keys(groupedPricing).length === 0 ? (
                                        <div className="text-center py-8">
                                            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                            <p className="text-slate-500">No cost items configured</p>
                                        </div>
                                    ) : (
                                        Object.entries(groupedPricing).map(([sectionName, items]) => (
                                            <div key={sectionName}>
                                                <h3 className="font-medium text-slate-900 mb-3">{sectionName}</h3>
                                                <div className="space-y-2">
                                                    {(items as any[]).map((item) => {
                                                        // Calculate summary stats only for FIXED items
                                                        let totalAmount: number | null = null;

                                                        // Watch values for live calculation, fallback to actual item values
                                                        const watchedUnitPrice = watch(`unitPrice_${item.costItemId}`);
                                                        const watchedQuantity = watch(`defaultQuantity_${item.costItemId}`);
                                                        const watchedAmount = watch(`amount_${item.costItemId}`);

                                                        const currentUnitPrice = watchedUnitPrice !== undefined && watchedUnitPrice !== '' && watchedUnitPrice !== null
                                                            ? parseFloat(watchedUnitPrice)
                                                            : (item.unitPrice || item.defaultUnitPriceConfig || 0);
                                                        const currentQuantity = watchedQuantity !== undefined && watchedQuantity !== '' && watchedQuantity !== null
                                                            ? parseFloat(watchedQuantity)
                                                            : (item.defaultQuantity || 0);
                                                        const currentAmount = watchedAmount !== undefined && watchedAmount !== '' && watchedAmount !== null
                                                            ? parseFloat(watchedAmount)
                                                            : (item.amount || item.defaultAmountConfig || 0);

                                                        if (item.sectionType === 'FIXED') {
                                                            if (item.unit === 'PER_UNIT') {
                                                                totalAmount = currentUnitPrice * currentQuantity;
                                                            } else {
                                                                totalAmount = currentAmount;
                                                            }
                                                        }

                                                        return (
                                                            <div
                                                                key={item.costItemId}
                                                                className="grid grid-cols-12 gap-4 items-start p-4 border border-slate-200 rounded-lg bg-white"
                                                            >
                                                                <div className="col-span-3">
                                                                    <p className="font-medium text-slate-900">{item.itemName}</p>
                                                                    <p className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{item.unit.replace('_', ' ')}</p>
                                                                </div>
                                                                {item.sectionType === 'FIXED' ? (
                                                                    <div className="col-span-9">
                                                                        {item.unit === 'PER_UNIT' ? (
                                                                            <div className="space-y-3">
                                                                                {/* Header Row */}
                                                                                <div className="grid grid-cols-12 gap-3 text-xs font-medium text-slate-500 uppercase">
                                                                                    <div className="col-span-3">Quantity</div>
                                                                                    <div className="col-span-5">Unit Rate</div>
                                                                                    <div className="col-span-4 text-right">Total</div>
                                                                                </div>

                                                                                {/* Default Values Row (if exists) */}
                                                                                {item.defaultUnitPriceConfig && (
                                                                                    <div className="grid grid-cols-12 gap-3 items-center text-sm">
                                                                                        <div className="col-span-3 text-slate-400">-</div>
                                                                                        <div className="col-span-5">
                                                                                            <span className="text-slate-700 font-medium">₹{item.defaultUnitPriceConfig}</span>
                                                                                            <span className="text-xs text-slate-400 ml-1.5">(Location Default)</span>
                                                                                        </div>
                                                                                        <div className="col-span-4"></div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Input Row */}
                                                                                <div className="grid grid-cols-12 gap-3 items-center">
                                                                                    <div className="col-span-3">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <input
                                                                                                type="number"
                                                                                                step="0.01"
                                                                                                className="input input-sm w-full"
                                                                                                placeholder="0"
                                                                                                defaultValue={item.defaultQuantity || ''}
                                                                                                {...register(`defaultQuantity_${item.costItemId}`)}
                                                                                            />
                                                                                            <span className="text-xs text-slate-500 shrink-0">units</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-span-5">
                                                                                        <div className="relative">
                                                                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                                                                                            <input
                                                                                                type="number"
                                                                                                step="0.01"
                                                                                                className="input input-sm pl-6 w-full"
                                                                                                placeholder={item.defaultUnitPriceConfig ? "Override" : "Rate"}
                                                                                                defaultValue={item.unitPrice || ''}
                                                                                                {...register(`unitPrice_${item.costItemId}`)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-span-4 text-right">
                                                                                        <p className="text-lg font-bold text-primary-600">
                                                                                            {formatCurrency(totalAmount || 0)}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            /* Per Day / Fixed Procedure */
                                                                            <div className="grid grid-cols-12 gap-4 items-start">
                                                                                <div className="col-span-6">
                                                                                    <label className="text-xs text-slate-500 font-medium uppercase mb-1.5 block">Amount</label>
                                                                                    {item.defaultAmountConfig ? (
                                                                                        <div className="space-y-1.5">
                                                                                            <div className="text-sm text-slate-700 font-medium">
                                                                                                ₹{item.defaultAmountConfig} <span className="text-xs text-slate-400 font-normal">(Default)</span>
                                                                                            </div>
                                                                                            <div className="relative">
                                                                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    step="0.01"
                                                                                                    className="input input-sm pl-6 w-full text-xs"
                                                                                                    placeholder="Override for this procedure"
                                                                                                    defaultValue={item.amount || ''}
                                                                                                    {...register(`amount_${item.costItemId}`)}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="relative">
                                                                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                                                                                            <input
                                                                                                type="number"
                                                                                                step="0.01"
                                                                                                className="input input-sm pl-6 w-full"
                                                                                                placeholder="Amount"
                                                                                                defaultValue={item.amount || ''}
                                                                                                {...register(`amount_${item.costItemId}`)}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div className="col-span-6 text-right">
                                                                                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1.5">
                                                                                        {item.unit === 'PER_DAY' ? 'Daily Rate' : 'Total'}
                                                                                    </p>
                                                                                    <p className="text-lg font-bold text-primary-600">
                                                                                        {formatCurrency(totalAmount || 0)}
                                                                                        {item.unit === 'PER_DAY' && <span className="text-xs font-normal text-slate-500 ml-1">/ day</span>}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    /* Percentage Based */
                                                                    <div className="col-span-9 max-w-md">
                                                                        <label className="text-xs text-slate-500 font-medium uppercase mb-1.5 block">Percentage</label>
                                                                        {item.defaultPercentageConfig ? (
                                                                            <div className="space-y-1.5">
                                                                                <div className="text-sm text-slate-700 font-medium">
                                                                                    {item.defaultPercentageConfig}% <span className="text-xs text-slate-400 font-normal">(Default)</span>
                                                                                </div>
                                                                                <div className="relative">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        className="input input-sm w-full text-xs"
                                                                                        placeholder="Override for this procedure"
                                                                                        defaultValue={item.percentage || ''}
                                                                                        {...register(`percentage_${item.costItemId}`)}
                                                                                    />
                                                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="relative">
                                                                                <input
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    className="input input-sm w-full"
                                                                                    placeholder="Percentage"
                                                                                    defaultValue={item.percentage || ''}
                                                                                    {...register(`percentage_${item.costItemId}`)}
                                                                                />
                                                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {Object.keys(groupedPricing).length > 0 && (
                                    <div className="card-footer flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={updatePricingMutation.isPending}
                                            className="btn btn-primary btn-md"
                                        >
                                            <Save className="w-4 h-4" />
                                            {updatePricingMutation.isPending ? 'Saving...' : 'Save Pricing'}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {/* Room Rates Tab */}
                    {activeTab === 'room-rates' && (
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Room Rate Overrides</h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Leave blank to use default rates. Set override to customize for this procedure.
                                </p>
                            </div>

                            <form key={`room-rates-form-${selectedConfigId}`} onSubmit={handleSubmit(onSubmitRoomRates)}>
                                <div className="card-body">
                                    {roomTypes.length === 0 ? (
                                        <div className="text-center py-8">
                                            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                            <p className="text-slate-500">No room types configured for this location</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                Go to Room Types to add room configurations
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {roomRates.map((room: any) => (
                                                <div
                                                    key={room.roomTypeId}
                                                    className="grid grid-cols-12 gap-4 items-center p-4 border border-slate-200 rounded-lg"
                                                >
                                                    <div className="col-span-4">
                                                        <p className="font-medium text-slate-700">{room.roomName}</p>
                                                        {room.overrideRate !== null && room.overrideRate !== undefined ? (
                                                            <div>
                                                                <p className="text-sm font-semibold text-primary-600">
                                                                    Current: {formatCurrency(room.overrideRate)}/day
                                                                    <span className="text-xs font-normal text-amber-600 ml-1">(Override)</span>
                                                                </p>
                                                                <p className="text-xs text-slate-400">
                                                                    Default: {formatCurrency(room.defaultRate)}/day
                                                                    {room.markupPercentage > 0 && (
                                                                        <span className="text-amber-600 ml-1">
                                                                            (+{room.markupPercentage}% markup on total)
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-slate-500">
                                                                Default: {formatCurrency(room.defaultRate)}/day
                                                                {room.markupPercentage > 0 && (
                                                                    <span className="text-amber-600 ml-2">
                                                                        (+{room.markupPercentage}% markup on total)
                                                                    </span>
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="col-span-8">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-slate-500">Override: ₹</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="input"
                                                                placeholder={`Leave blank for ${formatCurrency(room.defaultRate)}`}
                                                                defaultValue={room.overrideRate !== null && room.overrideRate !== undefined ? room.overrideRate : ''}
                                                                {...register(`override_${room.roomTypeId}`)}
                                                            />
                                                            <span className="text-sm text-slate-500">/day</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {roomTypes.length > 0 && (
                                    <div className="card-footer flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={updateRoomRatesMutation.isPending}
                                            className="btn btn-primary btn-md"
                                        >
                                            <Save className="w-4 h-4" />
                                            {updateRoomRatesMutation.isPending ? 'Saving...' : 'Save Room Rates'}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {/* ICU Rates Tab */}
                    {activeTab === 'icu-rates' && (
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">ICU Rate Overrides</h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Leave blank to use default ICU rates. Set override to customize for this procedure.
                                </p>
                            </div>

                            <form key={`icu-rates-form-${selectedConfigId}`} onSubmit={handleSubmit(onSubmitIcuRates)}>
                                <div className="card-body">
                                    {icuRates.length === 0 ? (
                                        <div className="text-center py-8">
                                            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                            <p className="text-slate-500">No ICU types configured for this location</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                Go to ICU Rates to add ICU configurations
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {icuRatesList.map((icu: any) => (
                                                <div
                                                    key={icu.icuRateId}
                                                    className="grid grid-cols-12 gap-4 items-center p-4 border border-slate-200 rounded-lg"
                                                >
                                                    <div className="col-span-4">
                                                        <p className="font-medium text-slate-700">{icu.icuName}</p>
                                                        {icu.description && (
                                                            <p className="text-xs text-slate-400 mb-1">{icu.description}</p>
                                                        )}
                                                        {icu.overrideRate !== null && icu.overrideRate !== undefined ? (
                                                            <div>
                                                                <p className="text-sm font-semibold text-primary-600">
                                                                    Current: {formatCurrency(icu.overrideRate)}/day
                                                                    <span className="text-xs font-normal text-amber-600 ml-1">(Override)</span>
                                                                </p>
                                                                <p className="text-xs text-slate-400">
                                                                    Default: {formatCurrency(icu.defaultRate)}/day
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-slate-500">
                                                                Default: {formatCurrency(icu.defaultRate)}/day
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="col-span-8">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-slate-500">Override: ₹</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="input"
                                                                placeholder={`Leave blank for ${formatCurrency(icu.defaultRate)}`}
                                                                defaultValue={icu.overrideRate !== null && icu.overrideRate !== undefined ? icu.overrideRate : ''}
                                                                {...register(`override_${icu.icuRateId}`)}
                                                            />
                                                            <span className="text-sm text-slate-500">/day</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {icuRates.length > 0 && (
                                    <div className="card-footer flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={updateIcuRatesMutation.isPending}
                                            className="btn btn-primary btn-md"
                                        >
                                            <Save className="w-4 h-4" />
                                            {updateIcuRatesMutation.isPending ? 'Saving...' : 'Save ICU Rates'}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
